import React, { useEffect, useState, useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, ReferenceLine, ReferenceDot } from 'recharts';
import { useAssessment } from '../components/assessmentContext';
import { CopyReviewAssessment, opportunityBubbleChart, updateOpportunityDataSingle, UserMaster_SelectAll } from '../apis/services/csaApi';
import en from "../json/en.json";
import ch from "../json/ch.json";
import ExportToExcel from './ExportToExcel';

const OpportunityBubbleChart = () => {
  const [data, setData] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("all");
  const [selectedEnabler, setSelectedEnabler] = useState("all");
  const [selectedMethodId, setSelectedMethodId] = useState(0);
  const [selectedEnablerId, setSelectedEnablerId] = useState(0);
  const uId = localStorage.getItem("uId") || "4";
  const lId = localStorage.getItem("languageId") || "1";
  const assessmentId = localStorage.getItem("AssessmentId");
  const user_Type = localStorage.getItem("userType");
  const { activeAssessment } = useAssessment();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [helpImage, setHelpImage] = useState("");
  const [users, setUsers] = useState([]);
  const [visibleBubbleCount, setVisibleBubbleCount] = useState(0);

  // Axis selection state
  // const [xAxis, setXAxis] = useState('time');
  // const [yAxis, setYAxis] = useState('currentseq');
  // const [zAxis, setZAxis] = useState('desiredseq');

  const [xAxis, setXAxis] = useState('currentseq'); // Default to Difficulty
  const [yAxis, setYAxis] = useState('desiredseq');  // Default to Importance
  const [zAxis, setZAxis] = useState('time');        // Default to Time

  // Axis options
  const axisOptions = [
    // { value: 'time', label: lId === "1" ? en.Time : ch.Time },
    { value: 'currentseq', label: lId === "1" ? en.Difficulty : ch.Difficulty },
    { value: 'desiredseq', label: lId === "1" ? en.Importance : ch.Importance },
    { value: 'time', label: lId === "1" ? en.Time : ch.Time },
  ];

  // Method and Enabler options
  const methodOptions = [
    { id: 1, name: lId === "1" ? en.Segmentation : ch.Segmentation },
    { id: 2, name: lId === "1" ? en.RGM : ch.RGM },
    { id: 3, name: lId === "1" ? en.RTM : ch.RTM },
    { id: 4, name: lId === "1" ? en.PACE : ch.PACE },
    { id: 5, name: lId === "1" ? en.EXECUTION : ch.EXECUTION },
    { id: 6, name: lId === "1" ? en.CVC : ch.CVC }
  ];

  const enablerOptions = [
    { id: 1, name: lId === "1" ? en.StandardizedProcessnGovernance : ch.StandardizedProcessnGovernance },
    { id: 2, name: lId === "1" ? en.HumanTalentnOrganization : ch.HumanTalentnOrganization },
    { id: 3, name: lId === "1" ? en.ToolsnTechnology : ch.ToolsnTechnology }
  ];

  // Fetch data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await UserMaster_SelectAll();
        setUsers(response);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
    Opportunity_BubbleChart();
  }, [assessmentId, selectedMethodId, selectedEnablerId]);

  const Opportunity_BubbleChart = async () => {
    try {
      const response = await opportunityBubbleChart(uId, assessmentId, selectedMethodId, selectedEnablerId, lId);
      setData(response);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
  };

  // Filter data based on selected method and enabler
  const filteredData = useMemo(() => {
    return data.filter(item => {
      if (selectedMethod !== "all" && item.method !== selectedMethod) return false;
      if (selectedEnabler !== "all" && item.enablers !== selectedEnabler) return false;
      return true;
    });
  }, [data, selectedMethod, selectedEnabler]);

  // Update visible bubble count
  useEffect(() => {
    setVisibleBubbleCount(
      filteredData.filter(item =>
        item.currentseq > 0 &&
        item.desiredseq > 0 &&
        item.time > 0
      ).length
    );
  }, [filteredData]);

  // Calculate quadrant counts based on current axis selection and new reference lines
  const getQuadrantCounts = useMemo(() => {
    const xLow = 5.1;
    const xHigh = 10.1;
    const yMid = 7.5;

    return {
      q1: filteredData.filter(item =>
        item[yAxis] >= yMid &&
        item[xAxis] <= xLow &&
        item[xAxis] > 0 &&
        item[yAxis] > 0 &&
        item[zAxis] > 0
      ).length,
      q2: filteredData.filter(item =>
        item[yAxis] >= yMid &&
        item[xAxis] > xLow &&
        item[xAxis] <= xHigh &&
        item[xAxis] > 0 &&
        item[yAxis] > 0 &&
        item[zAxis] > 0
      ).length,
      q3: filteredData.filter(item =>
        item[yAxis] >= yMid &&
        item[xAxis] > xHigh &&
        item[xAxis] > 0 &&
        item[yAxis] > 0 &&
        item[zAxis] > 0
      ).length,
      q4: filteredData.filter(item =>
        item[yAxis] < yMid &&
        item[xAxis] <= xLow &&
        item[xAxis] > 0 &&
        item[yAxis] > 0 &&
        item[zAxis] > 0
      ).length,
      q5: filteredData.filter(item =>
        item[yAxis] < yMid &&
        item[xAxis] > xLow &&
        item[xAxis] <= xHigh &&
        item[xAxis] > 0 &&
        item[yAxis] > 0 &&
        item[zAxis] > 0
      ).length,
      q6: filteredData.filter(item =>
        item[yAxis] < yMid &&
        item[xAxis] > xHigh &&
        item[xAxis] > 0 &&
        item[yAxis] > 0 &&
        item[zAxis] > 0
      ).length,
      total: filteredData.filter(item =>
        item[xAxis] > 0 &&
        item[yAxis] > 0 &&
        item[zAxis] > 0
      ).length
    };
  }, [filteredData, xAxis, yAxis, zAxis]);

  // Custom tooltip that shows values based on current axis selection
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded shadow border">
          {/* <p className="font-bold text-2xl">{lId === "1" ? en.Opportunity : ch.Opportunity}: {item.question_Seq}</p> */}
          <p className="font-bold text-2xl">{lId === "1" ? en.Opportunity : ch.Opportunity}: {item.qid}</p>
          <p className="text-xl">{item.questions}</p>
          <p className="text-xl">{lId === "1" ? en.Method : ch.Method}: {item.method}</p>
          <p className="text-xl">{lId === "1" ? en.enabler_ln : ch.enabler_ln}: {item.enablers}</p>
          <p className="text-xl">{axisOptions.find(o => o.value === xAxis)?.label}: {item[xAxis]}</p>
          <p className="text-xl">{axisOptions.find(o => o.value === yAxis)?.label}: {item[yAxis]}</p>
          <p className="text-xl">{axisOptions.find(o => o.value === zAxis)?.label}: {item[zAxis]}</p>
        </div>
      );
    }
    return null;
  };

  // Handle data changes
  const handleDataChange = async (item, field, value) => {
    const updatedValue = value === '' ? null : Number(value);

    // Update local state
    setData(prevData =>
      prevData.map(i =>
        i.index === item.index ? { ...i, [field]: updatedValue } : i
      )
    );

    // API call to save changes
    try {
      const payload = {
        Qid: item.qid,
        Opportunity: 0,
        Difficulty: field === 'currentseq' ? updatedValue || 0 : item.currentseq || 0,
        Important: field === 'desiredseq' ? updatedValue || 0 : item.desiredseq || 0,
        Time1: field === 'time' ? updatedValue || 0 : item.time || 0,
        Remarks: item.remarks || '',
        UserId: parseInt(uId),
        AssesmentID: parseInt(assessmentId),
        MethodId: item.methodid,
        EnablerId: item.enablerid
      };
      await updateOpportunityDataSingle(payload);
    } catch (error) {
      console.error("Error updating item:", error);
      // Revert on error
      setData(prevData =>
        prevData.map(i =>
          i.index === item.index ? item : i
        )
      );
      alert(lId === "1" ? "Error saving changes" : "保存更改时出错");
    }
  };

  // Handle method/enabler changes
  const handleMethodChange = (e) => {
    const value = e.target.value;
    setSelectedMethod(value);
    const method = methodOptions.find(m => m.name === value);
    setSelectedMethodId(method ? method.id : 0);
  };

  const handleEnablerChange = (e) => {
    const value = e.target.value;
    setSelectedEnabler(value);
    const enabler = enablerOptions.find(e => e.name === value);
    setSelectedEnablerId(enabler ? enabler.id : 0);
  };

  // Help modal component
  const HelpModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-3xl font-bold">{lId === "1" ? "Help" : "帮助"}</h3>
          <button onClick={() => setIsHelpModalOpen(false)} className="text-3xl font-bold">×</button>
        </div>
        <img src={helpImage} alt="Help" className="max-w-full h-auto" />
      </div>
    </div>
  );


  const calculateRank = (item) => {
    // item[xAxis] * item[yAxis] * item[zAxis]
    const current = item.currentseq ? Number(item.currentseq) : 0;
    const desired = item.desiredseq ? Number(item.desiredseq) : 0;
    const time = item.time ? Number(item.time) : 0;
    // const current = item[xAxis] ? Number(item[xAxis]) : 0;
    // const desired = item[yAxis] ? Number(item[yAxis]) : 0;
    // const time = item[zAxis] ? Number(item[zAxis]) : 0;

    // If all are 0 or null
    if (current === 0 && desired === 0 && time === 0) {
      return 1;
    }

    // Use 1 as default if any of them is 0 or null
    const c = current || 1;
    const d = desired || 1;
    const t = time || 1;

    return c * d * t;
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending',
  });

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      // Handle null/undefined values with appropriate defaults
      const getValue = (item, key) => {
        if (item[key] === null || item[key] === undefined) {
          // Default values based on data type
          // if (['currentseq', 'desiredseq', 'time', 'question_Seq', 'rank'].includes(key)) return 0;
          if (['currentseq', 'desiredseq', 'time', 'qid', 'rank'].includes(key)) return 0;
          if (['method', 'enablers', 'questions'].includes(key)) return '';
          // if (key === 'xAxisValue') return a[xAxis] || 0;
          // if (key === 'yAxisValue') return a[yAxis] || 0;
          // if (key === 'zAxisValue') return a[zAxis] || 0;
          return '';
        }
        return item[key];
      };

      const aValue = getValue(a, sortConfig.key);
      const bValue = getValue(b, sortConfig.key);

      // Special handling for Rank
      if (sortConfig.key === 'rank') {
        const aRank = calculateRank(a);
        const bRank = calculateRank(b);
        return sortConfig.direction === 'ascending' ? aRank - bRank : bRank - aRank;
      }

      // Handle axis values (they might be dynamic based on xAxis/yAxis/zAxis state)
      // if (['xAxisValue', 'yAxisValue', 'zAxisValue'].includes(sortConfig.key)) {
      //   const actualKey = sortConfig.key === 'xAxisValue' ? xAxis :
      //     sortConfig.key === 'yAxisValue' ? yAxis : zAxis;
      //   const aAxisValue = a[actualKey] || 0;
      //   const bAxisValue = b[actualKey] || 0;
      //   return sortConfig.direction === 'ascending'
      //     ? aAxisValue - bAxisValue
      //     : bAxisValue - aAxisValue;
      // }

      // Numeric comparison for numeric fields
      // if (['currentseq', 'desiredseq', 'time', 'question_Seq'].includes(sortConfig.key)) {
      if (['currentseq', 'desiredseq', 'time', 'qid'].includes(sortConfig.key)) {
        return sortConfig.direction === 'ascending'
          ? aValue - bValue
          : bValue - aValue;
      }

      // String comparison for text fields (case insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aStr = aValue.toLowerCase();
        const bStr = bValue.toLowerCase();
        if (aStr < bStr) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      }

      // Fallback for other types
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  // }, [filteredData, sortConfig, xAxis, yAxis, zAxis]); // Added axis dependencies
  }, [filteredData, sortConfig]);
  
  const chartRef = React.useRef(null);
  return (
    <div className="wrapper">
      <div className="mx-40 my-40 flex flex-col w-full gap-6">
        {/* Filter controls */}
        <div className="bg-white rounded-xl shadow-md p-4 flex justify-between items-end w-full">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-2xl font-medium text-gray-700 mb-1">{lId === "1" ? en.Method : ch.Method}</label>
                <select
                  value={selectedMethod}
                  onChange={handleMethodChange}
                  className="w-full p-2 border rounded text-2xl"
                >
                  <option value="all">{lId === "1" ? en.All : ch.All}</option>
                  {methodOptions.map(method => (
                    <option key={method.id} value={method.name}>{method.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-2xl font-medium text-gray-700 mb-1">{lId === "1" ? en.enabler_ln : ch.enabler_ln}</label>
                <select
                  value={selectedEnabler}
                  onChange={handleEnablerChange}
                  className="w-full p-2 border rounded text-2xl"
                >
                  <option value="all">{lId === "1" ? en.All : ch.All}</option>
                  {enablerOptions.map(enabler => (
                    <option key={enabler.id} value={enabler.name}>{enabler.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex items-end gap-2 mb-2 mr-2">
              <div className="text-2xl">
                {lId === "1" ? "Opportunities #" : "机会 #"}
                <span className="font-bold">{visibleBubbleCount}</span>
              </div>
            </div>
            {/* <ExportToExcel data={filteredData} languageId={lId} en={en} ch={ch} /> */}

            <ExportToExcel
              data={filteredData}
              languageId={lId}
              en={en}
              ch={ch}
              chartRef={chartRef}
            />

            <button
              onClick={() => {
                setHelpImage(lId === "1" ? "/assets/help/help.png" : "/assets/help/help_cn.png");
                setIsHelpModalOpen(true);
              }}
              className="bg-blue-500 text-white p-2 rounded text-2xl h-12 w-12 flex items-center justify-center"
              title={lId === "1" ? "Help" : "帮助"}
            >
              ?
            </button>
          </div>
        </div>

         {/* Chart section */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="h-[630px]" ref={chartRef}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                {/* Updated reference lines */}
                <ReferenceLine x={5.0} stroke="#999" strokeDasharray="3 3" />
                <ReferenceLine x={10.0} stroke="#999" strokeDasharray="3 3" />
                <ReferenceLine y={7.5} stroke="#999" strokeDasharray="3 3" />

                {/* Dynamic Axes */}
                <XAxis
                  type="number"
                  dataKey={xAxis}
                  name={axisOptions.find(o => o.value === xAxis)?.label}
                  label={{
                    value: axisOptions.find(o => o.value === xAxis)?.label,
                    position: 'bottom',
                    fontSize: 18
                  }}
                  domain={[0, 16]}
                  tickCount={17}
                  tick={{ fontSize: 20 }}
                  axisLine={{ stroke: '#333' }}
                />

                <YAxis
                  type="number"
                  dataKey={yAxis}
                  name={axisOptions.find(o => o.value === yAxis)?.label}
                  label={{
                    value: axisOptions.find(o => o.value === yAxis)?.label,
                    angle: -90,
                    position: 'left',
                    fontSize: 18
                  }}
                  domain={[0, 17]}
                  tickCount={18}
                  tick={{ fontSize: 20 }}
                  axisLine={{ stroke: '#333' }}
                />

                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <ZAxis dataKey={zAxis} scale="linear" range={[50, 12000]} />
                <Tooltip content={<CustomTooltip />} />

                {/* Quadrant labels with counts - updated for 6 quadrants */}
                {/* <ReferenceDot x={2.5} y={11.25} r={0} isFront={true}>
                  <Label
                    value={getQuadrantCounts.q1===0?null:getQuadrantCounts.q1}
                    position="center"
                    fill="blue"
                    fontSize={24}
                    fontWeight="bold"
                  />
                </ReferenceDot>
                <ReferenceDot x={7.5} y={11.25} r={0} isFront={true}>
                  <Label
                    value={getQuadrantCounts.q2===0?null:getQuadrantCounts.q2}
                    position="center"
                    fill="blue"
                    fontSize={24}
                    fontWeight="bold"
                  />
                </ReferenceDot>
                <ReferenceDot x={12.5} y={11.25} r={0} isFront={true}>
                  <Label
                    value={getQuadrantCounts.q3===0?null:getQuadrantCounts.q3}
                    position="center"
                    fill="blue"
                    fontSize={24}
                    fontWeight="bold"
                  />
                </ReferenceDot>
                <ReferenceDot x={2.5} y={3.75} r={0} isFront={true}>
                  <Label
                    value={getQuadrantCounts.q4===0?null:getQuadrantCounts.q4}
                    position="center"
                    fill="blue"
                    fontSize={24}
                    fontWeight="bold"
                  />
                </ReferenceDot>
                <ReferenceDot x={7.5} y={3.75} r={0} isFront={true}>
                  <Label
                    value={getQuadrantCounts.q5===0?null:getQuadrantCounts.q5}
                    position="center"
                    fill="blue"
                    fontSize={24}
                    fontWeight="bold"
                  />
                </ReferenceDot>
                <ReferenceDot x={12.5} y={3.75} r={0} isFront={true}>
                  <Label
                    value={getQuadrantCounts.q6===0?null:getQuadrantCounts.q6}
                    position="center"
                    fill="blue"
                    fontSize={24}
                    fontWeight="bold"
                  />
                </ReferenceDot> */}

                <Scatter
                  name="Opportunities"
                  data={filteredData.filter(item =>
                    item[xAxis] > 0 &&
                    item[yAxis] > 0 &&
                    item[zAxis] > 0
                  )}
                  fill="none"
                  stroke="black"
                  strokeWidth={2}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Axis selection controls */}
          <div className="flex gap-4 mt-4 ml-4 text-2xl">
            <div>
              <label className='text-2xl font-medium text-gray-700'>X-Axis:</label>
              <select
                value={xAxis}
                onChange={(e) => setXAxis(e.target.value)}
                className="text-2xl font-medium text-gray-400 ml-2 p-1 border rounded"
              >
                {axisOptions.map(opt => (
                  <option key={`x-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className='text-2xl font-medium text-gray-700'>Y-Axis:</label>
              <select
                value={yAxis}
                onChange={(e) => setYAxis(e.target.value)}
                className="text-2xl font-medium text-gray-400 ml-2 p-1 border rounded"
              >
                {axisOptions.map(opt => (
                  <option key={`y-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className='text-2xl font-medium text-gray-700'>Size (Z-Axis):</label>
              <select
                value={zAxis}
                onChange={(e) => setZAxis(e.target.value)}
                className="text-2xl font-medium text-gray-400 ml-2 p-1 border rounded"
              >
                {axisOptions.map(opt => (
                  <option key={`z-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Data table section */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th style={{ width: 72 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('qid')}
                  >
                    {lId === "1" ? en.QID : ch.QID}
                    {sortConfig.key === 'qid'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 115 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('method')}
                  >
                    {lId === "1" ? en.Method : ch.Method}
                    {sortConfig.key === 'method'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 130 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('enablers')}
                  >
                    {lId === "1" ? en.enabler_ln : ch.enabler_ln}
                    {sortConfig.key === 'enablers'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 300 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('questions')}
                  >
                    {lId === "1" ? en.question : ch.question}
                    {sortConfig.key === 'questions'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 180 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('desired')}
                  >
                    {lId === "1" ? en.desired : ch.desired}
                    {/* {sortConfig.key === 'desired' && (
                      <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
                    )} */}
                    {sortConfig.key === 'desired'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 154 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('currentseq')}
                  >
                    {lId === "1" ? en.Difficulty : ch.Difficulty}
                    {sortConfig.key === 'currentseq'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 154 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('desiredseq')}
                  >
                    {lId === "1" ? en.Importance : ch.Importance}
                    {sortConfig.key === 'desiredseq'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 154 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('time')}
                  >
                    {lId === "1" ? en.Time : ch.Time}
                    {sortConfig.key === 'time'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 90 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('rank')}
                  >
                    {lId === "1" ? "Rank" : "排名"}
                    {sortConfig.key === 'rank'
                      ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓')
                      : ' ↕'}
                  </th>

                  <th style={{ width: 200 }}
                    className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase"
                  >
                    {lId === "1" ? en.remarks : ch.remarks}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((item) => (
                  <tr key={item.index}>
                    <td className="px-4 py-2 whitespace-nowrap text-2xl text-gray-900">{item.qid}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-2xl text-gray-900">{item.method}</td>
                    <td className="px-4 py-2 whitespace-normal text-2xl text-gray-900">{item.enablers}</td>
                    <td className="px-4 py-2 whitespace-normal text-2xl text-gray-900">{item.questions}</td>
                    <td className="px-4 py-2 whitespace-normal text-2xl text-gray-900">{item.desired}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <select
                        value={item.currentseq || ''}
                        onChange={(e) => handleDataChange(item, 'currentseq', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-2xl"
                      >
                        <option value="">{lId === "1" ? en.Select : ch.Select}</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(num => (
                          <option key={`currentseq-${num}`} value={num}>{num}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <select
                        value={item.desiredseq || ''}
                        onChange={(e) => handleDataChange(item, 'desiredseq', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-2xl"
                      >
                        <option value="">{lId === "1" ? en.Select : ch.Select}</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(num => (
                          <option key={`desiredseq-${num}`} value={num}>{num}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <select
                        value={item.time || ''}
                        onChange={(e) => handleDataChange(item, 'time', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-2xl"
                      >
                        <option value="">{lId === "1" ? en.Select : ch.Select}</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(num => (
                          <option key={`time-${num}`} value={num}>{num}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-2xl text-center">
                      {calculateRank(item)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <input
                        type="text"
                        value={item.remarks || ''}
                        onChange={(e) => handleDataChange(item, 'remarks', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-2xl"
                        placeholder={lId === "1" ? en.EnterRemarks : ch.EnterRemarks}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Matrix and Summary sections - updated for 6 quadrants */}
        {assessmentId === "2" && user_Type === "Bottler" && (
          <div className="bg-white rounded-xl shadow-md p-4 mt-6 flex">
            <div className="w-2/4 pr-6">
              <div className="bg-white rounded-xl shadow-md">
                <h2 className="text-4xl font-semibold mb-4">{lId === "1" ? en.OpportunityCountMatrix : ch.OpportunityCountMatrix}</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100" rowSpan="2">
                          {axisOptions.find(o => o.value === yAxis)?.label}
                        </th>
                        <th className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100" colSpan="3">
                          {axisOptions.find(o => o.value === xAxis)?.label}
                        </th>
                      </tr>
                      <tr>
                        <th className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100">1-5</th>
                        <th className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100">6-10</th>
                        <th className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100">11-15</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border px-4 py-2 text-2xl text-center font-medium text-gray-700 bg-gray-100">8-15</td>
                        <td className="border px-4 py-2 text-2xl text-center bg-blue-100">
                          {getQuadrantCounts.q1}
                        </td>
                        <td className="border px-4 py-2 text-2xl text-center bg-yellow-100">
                          {getQuadrantCounts.q2}
                        </td>
                        <td className="border px-4 py-2 text-2xl text-center bg-red-100">
                          {getQuadrantCounts.q3}
                        </td>
                      </tr>
                      <tr>
                        <td className="border px-4 py-2 text-2xl text-center font-medium text-gray-700 bg-gray-100">1-7</td>
                        <td className="border px-4 py-2 text-2xl text-center bg-green-100">
                          {getQuadrantCounts.q4}
                        </td>
                        <td className="border px-4 py-2 text-2xl text-center bg-purple-100">
                          {getQuadrantCounts.q5}
                        </td>
                        <td className="border px-4 py-2 text-2xl text-center bg-gray-300">
                          {getQuadrantCounts.q6}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-end w-2/4">
              <h2 className="text-4xl font-semibold mb-4">{lId === "1" ? en.QuadrantSummary : ch.QuadrantSummary}</h2>
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border border-gray-400 px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-200">
                      {lId === "1" ? en.Quadrant : ch.Quadrant}
                    </th>
                    <th className="border border-gray-400 px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-200">
                      {lId === "1" ? en.Count : ch.Count}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2 text-2xl bg-blue-100">
                      {lId === "1" ? "High Value, Low Difficulty" : "高重要性，低难度"}
                    </td>
                    <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-blue-100">
                      {getQuadrantCounts.q1}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2 text-2xl bg-yellow-100">
                      {lId === "1" ? "High Value, Medium Difficulty" : "高重要性，中等难度"}
                    </td>
                    <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-yellow-100">
                      {getQuadrantCounts.q2}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2 text-2xl bg-red-100">
                      {lId === "1" ? "High Value, High Difficulty" : "高重要性，高难度"}
                    </td>
                    <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-red-100">
                      {getQuadrantCounts.q3}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2 text-2xl bg-green-100">
                      {lId === "1" ? "Low Value, Low Difficulty" : "低重要性，低难度"}
                    </td>
                    <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-green-100">
                      {getQuadrantCounts.q4}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2 text-2xl bg-purple-100">
                      {lId === "1" ? "Low Value, Medium Difficulty" : "低价值，中等难度"}
                    </td>
                    <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-purple-100">
                      {getQuadrantCounts.q5}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2 text-2xl bg-gray-100">
                      {lId === "1" ? "Low Value, High Difficulty" : "低价值，高难度"}
                    </td>
                    <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-gray-100">
                      {getQuadrantCounts.q6}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2 text-2xl font-medium bg-gray-200">
                      {lId === "1" ? en.total : ch.total}
                    </td>
                    <td className="border border-gray-400 px-4 py-2 text-2xl text-center font-medium bg-gray-200">
                      {getQuadrantCounts.total}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Help modal */}
      {isHelpModalOpen && <HelpModal />}
    </div>
  );
};

export default OpportunityBubbleChart;


// old axis
// import React, { useEffect, useState } from 'react';
// import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, ReferenceLine, ReferenceDot, Text } from 'recharts';
// import { useAssessment } from '../components/assessmentContext';
// import { CopyReviewAssessment, opportunityBubbleChart, updateOpportunityDataSingle, UserMaster_SelectAll } from '../apis/services/csaApi';
// import en from "../json/en.json";
// import ch from "../json/ch.json";
// import ExportToExcel from './ExportToExcel';

// const OpportunityBubbleChart = () => {
//   const [data, setData] = useState([]);
//   const [selectedMethod, setSelectedMethod] = useState("all");
//   const [selectedEnabler, setSelectedEnabler] = useState("all");
//   const [filterDifficulty, setFilterDifficulty] = useState(null);
//   const [filterImportance, setFilterImportance] = useState(null);
//   const [selectedMethodId, setSelectedMethodId] = useState(0);
//   const [selectedEnablerId, setSelectedEnablerId] = useState(0);
//   const [isSaving, setIsSaving] = useState(false);
//   const uId = localStorage.getItem("uId") || "4";
//   const lId = localStorage.getItem("languageId") || "1";
//   const assessmentId = localStorage.getItem("AssessmentId");
//   const user_Type = localStorage.getItem("userType");
//   const { activeAssessment } = useAssessment();
//   const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
//   const [helpImage, setHelpImage] = useState("");
//   const [users, setUsers] = useState([]);
//   const [selectedUserId, setSelectedUserId] = useState('');
//   const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
//   const [showConfirmationModal, setShowConfirmationModal] = useState(false);
//   const [visibleBubbleCount, setVisibleBubbleCount] = useState(0);
//   const [hiddenBubbleCount, setHiddenBubbleCount] = useState(0);

//   // Simplified axis selection state
//   const [xAxis, setXAxis] = useState('time');
//   const [yAxis, setYAxis] = useState('currentseq');
//   const [zAxis, setZAxis] = useState('desiredseq');

//   // Axis options
//   const axisOptions = [
//     { value: 'time', label: lId === "1" ? en.Time : ch.Time },
//     { value: 'currentseq', label: lId === "1" ? en.Difficulty : ch.Difficulty },
//     { value: 'desiredseq', label: lId === "1" ? en.Importance : ch.Importance }
//   ];

//   // useEffect to fetch users when component mounts
//   useEffect(() => {
//     const fetchUsers = async () => {
//       try {
//         const response = await UserMaster_SelectAll();
//         setUsers(response);
//       } catch (error) {
//         console.error("Error fetching users:", error);
//       }
//     };
//     fetchUsers();
//   }, []);

//   const HelpModal = () => (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white p-6 rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
//         <div className="flex justify-between items-center mb-4">
//           <h3 className="text-3xl font-bold">{lId === "1" ? "Help" : "帮助"}</h3>
//           <button
//             onClick={() => setIsHelpModalOpen(false)}
//             className="text-3xl font-bold"
//           >
//             ×
//           </button>
//         </div>
//         <img
//           src={helpImage}
//           alt="Help"
//           className="max-w-full h-auto"
//         />
//       </div>
//     </div>
//   );

//   const methodOptions = [
//     { id: 1, name: lId === "1" ? en.Segmentation : ch.Segmentation },
//     { id: 2, name: lId === "1" ? en.RGM : ch.RGM },
//     { id: 3, name: lId === "1" ? en.RTM : ch.RTM },
//     { id: 4, name: lId === "1" ? en.PACE : ch.PACE },
//     { id: 5, name: lId === "1" ? en.EXECUTION : ch.EXECUTION },
//     { id: 6, name: lId === "1" ? en.CVC : ch.CVC }
//   ];

//   const enablerOptions = [
//     { id: 1, name: lId === "1" ? en.StandardizedProcessnGovernance : ch.StandardizedProcessnGovernance },
//     { id: 2, name: lId === "1" ? en.HumanTalentnOrganization : ch.HumanTalentnOrganization },
//     { id: 3, name: lId === "1" ? en.ToolsnTechnology : ch.ToolsnTechnology }
//   ];

//   useEffect(() => {
//     Opportunity_BubbleChart();
//   }, [assessmentId, selectedMethodId, selectedEnablerId]);

//   const Opportunity_BubbleChart = async () => {
//     try {
//       const response = await opportunityBubbleChart(uId, assessmentId, selectedMethodId, selectedEnablerId, lId);
//       console.log("OpportunityBubbleChart data", response);
//       setData(response);
//     } catch (error) {
//       console.error("Error fetching data:", error);
//       setData([]);
//     }
//   };

//   const handleDataChange = async (_item, field, value) => {

//     const isNumericField = ['currentseq', 'desiredseq', 'time'].includes(field);

//     const updatedValue = value === ''
//       ? null
//       : isNumericField
//         ? Number(value)
//         : value; // Don't convert remarks to number

//     // Update local state
//     setData(prevData =>
//       prevData.map(item =>
//         item.index === _item.index ? { ...item, [field]: updatedValue } : item
//       )
//     );

//     const updatedItem = {
//       ..._item,
//       [field]: updatedValue
//     };

//     // First update local state
//     // setData(prevData =>
//     //   prevData.map(item =>
//     //     item.index === _item.index ? { ...item, [field]: value === '' ? null : Number(value) } : item
//     //   )
//     // );

//     // Prepare the updated item
//     // const updatedItem = {
//     //   ..._item,
//     //   [field]: value === '' ? null : Number(value)
//     // };

//     // Call API to save this single change
//     try {
//       const payload = {
//         Qid: updatedItem.qid,
//         Opportunity: 0,
//         Difficulty: updatedItem.currentseq ? updatedItem.currentseq : 0,
//         Important: updatedItem.desiredseq ? updatedItem.desiredseq : 0,
//         Time1: updatedItem.time ? updatedItem.time : 0,
//         Remarks: updatedItem.remarks || '',
//         UserId: parseInt(uId),
//         AssesmentID: parseInt(assessmentId),
//         MethodId: updatedItem.methodid,
//         EnablerId: updatedItem.enablerid
//       };

//       await updateOpportunityDataSingle(payload);
//       console.log("Update successful for item:", updatedItem.index);
//     } catch (error) {
//       console.error("Error updating item:", updatedItem.index, error);
//       // Revert local state if API call fails
//       setData(prevData =>
//         prevData.map(item =>
//           item.index === _item.index ? _item : item
//         )
//       );
//       alert("Error saving changes. Please try again.");
//     }
//   };

//   const handleMethodChange = (e) => {
//     const value = e.target.value;
//     setSelectedMethod(value);
//     const method = methodOptions.find(m => m.name === value);
//     setSelectedMethodId(method ? method.id : 0);
//   };

//   const handleEnablerChange = (e) => {
//     const value = e.target.value;
//     setSelectedEnabler(value);
//     const enabler = enablerOptions.find(e => e.name === value);
//     setSelectedEnablerId(enabler ? enabler.id : 0);
//   };

//   const handleFilterChange = (e) => {
//     const value = e.target.value;
//     if (value === "all") {
//       setFilterDifficulty(null);
//       setFilterImportance(null);
//     } else {
//       const [difficulty, importance] = value.split("-");
//       setFilterDifficulty(Number(difficulty));
//       setFilterImportance(Number(importance));
//     }
//   };

//   const filteredData = data.filter(item => {
//     // if (item.currentseq === 0 || item.desiredseq === 0 || item.time === 0) return false;
//     if (selectedMethod !== "all" && item.method !== selectedMethod) return false;
//     if (selectedEnabler !== "all" && item.enablers !== selectedEnabler) return false;
//     if (filterDifficulty !== null && filterImportance !== null) {
//       return item.currentseq === filterDifficulty && item.desiredseq === filterImportance;
//     }
//     return true;
//   });

//   useEffect(() => {
//     const totalItems = data.length;
//     const visibleItems = filteredData.filter(item =>
//       item.currentseq > 0 && item.desiredseq > 0 && item.time > 0
//     ).length;
//     setVisibleBubbleCount(visibleItems);
//     setHiddenBubbleCount(totalItems - visibleItems);
//   }, [filteredData, data]);


//   const CustomTooltip = ({ active, payload }) => {
//     if (active && payload && payload.length) {
//       const data = payload[0].payload;
//       return (
//         <div className="bg-white p-3 rounded shadow border">
//           <p className="font-bold text-2xl">{lId === "1" ? en.Opportunity : ch.Opportunity}: {data.qid}</p>
//           <p className="text-xl">{data.questions}</p>
//           <p className="text-xl">{lId === "1" ? en.Method : ch.Method}: {data.method}</p>
//           <p className="text-xl">{lId === "1" ? en.enabler_ln : ch.enabler_ln}: {data.enablers}</p>
//           <p className="text-xl">{lId === "1" ? en.Difficulty : ch.Difficulty}: {data.currentseq}</p>
//           <p className="text-xl">{lId === "1" ? en.Importance : ch.Importance}: {data.desiredseq}</p>
//           <p className="text-xl">{lId === "1" ? en.Time : ch.Time}: {data.time}</p>
//         </div>
//       );
//     }
//     return null;
//   };

//   // Generate filter options
//   const filterOptions = [];
//   for (let d = 1; d <= 5; d++) {
//     for (let i = 1; i <= 5; i++) {
//       filterOptions.push(`${d}-${i}`);
//     }
//   }



//   // Add this function to handle the copy operation
//   const handleCopyAssessment = async () => {
//     try {
//       const payload = {
//         UserIdFrom: parseInt(uId),
//         AssesmentIDFrom: parseInt(assessmentId),
//         UserIdTo: parseInt(selectedUserId),
//         AssesmentIDTo: parseInt(selectedAssessmentId)
//       };

//       await CopyReviewAssessment(payload);
//       alert("Assessment copied successfully!");
//       setShowConfirmationModal(false);
//       setSelectedUserId('');
//       setSelectedAssessmentId('');
//     } catch (error) {
//       console.error("Error copying assessment:", error);
//       alert("Error copying assessment. Please try again.");
//     }
//   };

//   // Add this confirmation modal component
//   const ConfirmationModal = () => (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white p-6 rounded-lg max-w-md">
//         <h3 className="text-3xl font-bold mb-4">
//           {lId === "1" ? "Confirm Copy" : "确认复制"}
//         </h3>
//         <p className="text-2xl mb-6">
//           {lId === "1"
//             ? "Are you sure you want to copy this assessment to the selected user?"
//             : "您确定要将此评估复制到所选用户吗？"}
//         </p>
//         <div className="flex justify-end gap-4">
//           <button
//             onClick={() => setShowConfirmationModal(false)}
//             className="px-4 py-2 bg-gray-300 rounded text-2xl"
//           >
//             {lId === "1" ? "Cancel" : "取消"}
//           </button>
//           <button
//             onClick={handleCopyAssessment}
//             className="px-4 py-2 bg-blue-500 text-white rounded text-2xl"
//           >
//             {lId === "1" ? "OK" : "确定"}
//           </button>
//         </div>
//       </div>
//     </div>
//   );

//   // const calculateRank = (item) => {
//   //   const current = item.currentseq || 0;
//   //   const desired = item.desiredseq || 0;
//   //   const time = item.time || 0;

//   //   if (current === 0 || desired === 0 || time === 0) {
//   //     return 1;
//   //   }
//   //   return current * desired * time;
//   // };


//   const calculateRank = (item) => {
//     const current = item.currentseq ? Number(item.currentseq) : 0;
//     const desired = item.desiredseq ? Number(item.desiredseq) : 0;
//     const time = item.time ? Number(item.time) : 0;

//     // If all are 0 or null
//     if (current === 0 && desired === 0 && time === 0) {
//       return 1;
//     }

//     // Use 1 as default if any of them is 0 or null
//     const c = current || 1;
//     const d = desired || 1;
//     const t = time || 1;

//     return c * d * t;
//   };


//   // sorting

//   const [sortConfig, setSortConfig] = useState({
//     key: null,
//     direction: 'ascending',
//   });

//   const requestSort = (key) => {
//     let direction = 'ascending';
//     if (sortConfig.key === key && sortConfig.direction === 'ascending') {
//       direction = 'descending';
//     }
//     setSortConfig({ key, direction });
//   };

//   const sortedData = React.useMemo(() => {
//     if (!sortConfig.key) return filteredData;

//     return [...filteredData].sort((a, b) => {
//       // Handle null/undefined values
//       const aValue = a[sortConfig.key] || 0;
//       const bValue = b[sortConfig.key] || 0;

//       // Special handling for Rank since it's calculated
//       if (sortConfig.key === 'rank') {
//         const aRank = calculateRank(a);
//         const bRank = calculateRank(b);
//         return sortConfig.direction === 'ascending' ? aRank - bRank : bRank - aRank;
//       }

//       // Numeric comparison for numeric fields
//       if (['currentseq', 'desiredseq', 'time', 'qid'].includes(sortConfig.key)) {
//         return sortConfig.direction === 'ascending'
//           ? aValue - bValue
//           : bValue - aValue;
//       }

//       // String comparison for text fields
//       if (aValue < bValue) {
//         return sortConfig.direction === 'ascending' ? -1 : 1;
//       }
//       if (aValue > bValue) {
//         return sortConfig.direction === 'ascending' ? 1 : -1;
//       }
//       return 0;
//     });
//   }, [filteredData, sortConfig]);


//   return (
//     <div className="wrapper">
//       <div className="mx-40 my-40 flex flex-col w-full gap-6">
//         {/* Filter controls section */}
//         {/* <div className="bg-white rounded-xl shadow-md p-4">
//           <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//             <div>
//               <label className="block text-2xl font-medium text-gray-700 mb-1">{lId==="1"?en.Method:ch.Method}</label>
//               <select
//                 value={selectedMethod}
//                 onChange={handleMethodChange}
//                 className="w-full p-2 border rounded text-2xl"
//               >
//                 <option value="all">{lId==="1"?en.All:ch.All}</option>
//                 {methodOptions.map(method => (
//                   <option key={method.id} value={method.name}>{method.name}</option>
//                 ))}
//               </select>
//             </div>
//             <div>
//               <label className="block text-2xl font-medium text-gray-700 mb-1">{lId==="1"?en.enabler_text:ch.enabler_text}</label>
//               <select
//                 value={selectedEnabler}
//                 onChange={handleEnablerChange}
//                 className="w-full p-2 border rounded text-2xl"
//               >
//                 <option value="all">{lId==="1"?en.All:ch.All}</option>
//                 {enablerOptions.map(enabler => (
//                   <option key={enabler.id} value={enabler.name}>{enabler.name}</option>
//                 ))}
//               </select>
//             </div>
//             <div>
//               <label className="block text-2xl font-medium text-gray-700 mb-1">Filter (Difficulty-Importance)</label>
//               <select
//                 onChange={handleFilterChange}
//                 className="w-full p-2 border rounded text-2xl"
//               >
//                 <option value="all">All Difficulty-Importance</option>
//                 {filterOptions.map(option => (
//                   <option key={option} value={option}>
//                     {option.split("-").join("-")}
//                   </option>
//                 ))}
//               </select>
//             </div>
//           </div>
//         </div> */}

//         <div className="bg-white rounded-xl shadow-md p-4 flex justify-between items-end w-full">
//           <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//             <div className="flex items-end gap-2">
//               <div className="flex-1">
//                 <label className="block text-2xl font-medium text-gray-700 mb-1">{lId === "1" ? en.Method : ch.Method}</label>
//                 <select
//                   value={selectedMethod}
//                   onChange={handleMethodChange}
//                   className="w-full p-2 border rounded text-2xl"
//                 >
//                   <option value="all">{lId === "1" ? en.All : ch.All}</option>
//                   {methodOptions.map(method => (
//                     <option key={method.id} value={method.name}>{method.name}</option>
//                   ))}
//                 </select>
//               </div>

//             </div>
//             <div className="flex items-end gap-2">
//               <div className="flex-1">
//                 <label className="block text-2xl font-medium text-gray-700 mb-1">{lId === "1" ? en.enabler_ln : ch.enabler_ln}</label>
//                 <select
//                   value={selectedEnabler}
//                   onChange={handleEnablerChange}
//                   className="w-full p-2 border rounded text-2xl"
//                 >
//                   <option value="all">{lId === "1" ? en.All : ch.All}</option>
//                   {enablerOptions.map(enabler => (
//                     <option key={enabler.id} value={enabler.name}>{enabler.name}</option>
//                   ))}
//                 </select>
//               </div>

//             </div>


//           </div>

//           <div className="flex gap-2">
//             <div className="flex items-end gap-2 mb-2 mr-2">
//               <div className="text-2xl">
//                 {lId === "1" ? "Opportunities #" : "机会 #"}
//                 <span className="font-bold">{visibleBubbleCount}</span>
//               </div>
//               {/* <div className="text-2xl">
//                 {lId === "1" ? "Bubbles hidden: " : "隐藏的气泡: "}
//                 <span className="font-bold">{hiddenBubbleCount}</span>
//                 {hiddenBubbleCount > 0 && (
//                   <span className="text-sm ml-2">
//                     ({lId === "1" ? "due to missing values" : "由于缺少值"})
//                   </span>
//                 )}
//               </div> */}
//             </div>
//             <ExportToExcel
//               data={filteredData}
//               languageId={lId}
//               en={en}
//               ch={ch}
//             />
//             <button
//               onClick={() => {
//                 setHelpImage(lId === "1" ? "/assets/help/help.png" : "/assets/help/help_cn.png");
//                 setIsHelpModalOpen(true);
//               }}
//               className="bg-blue-500 text-white p-2 rounded text-2xl h-12 w-12 flex items-center justify-center"
//               title={lId === "1" ? "Help" : "帮助"}
//             >
//               ?
//             </button>
//           </div>
//         </div>

//         {/* Chart section */}
//         <div className="bg-white rounded-xl shadow-md p-4">
//           <div className="h-[600px]">
//             <ResponsiveContainer width="100%" height="100%">
//               <ScatterChart
//                 margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
//               >
//                 <ReferenceLine x={2.1} stroke="#999" strokeDasharray="3 3" />
//                 <ReferenceLine y={3.1} stroke="#999" strokeDasharray="3 3" />

//                 {/* X Axis - Time */}
//                 <XAxis
//                   type="number"
//                   // dataKey="time"
//                   // name="Time"
//                   // label={{ value: lId === "1" ? en.Time : ch.Time, position: 'bottom', fontSize: 18 }}
//                   // domain={[0, 5]}
//                   dataKey={xAxis}
//                   name={axisOptions.find(o => o.value === xAxis)?.label}
//                   label={{ value: axisOptions.find(o => o.value === xAxis)?.label, position: 'bottom', fontSize: 18 }}
//                   domain={[0, 5]}
//                   tickCount={6}
//                   tick={{ fontSize: 20 }}
//                   axisLine={{ stroke: '#333' }}
//                 />

//                 {/* Y Axis - Difficulty */}
//                 <YAxis
//                   type="number"
//                   // dataKey="currentseq"
//                   // name="Difficulty"
//                   // label={{ value: lId === "1" ? en.Difficulty : ch.Difficulty, angle: -90, position: 'left', fontSize: 18 }}
//                   // domain={[0, 5]}
//                   dataKey={yAxis}
//                   name={axisOptions.find(o => o.value === yAxis)?.label}
//                   label={{ value: axisOptions.find(o => o.value === yAxis)?.label, angle: -90, position: 'left', fontSize: 18 }}
//                   domain={[0, 5]}
//                   tickCount={6}
//                   tick={{ fontSize: 20 }}
//                   axisLine={{ stroke: '#333' }}
//                 />

//                 {/* <XAxis
//                   type="number"
//                   dataKey="currentseq"
//                   name="Difficulty"
//                   label={{ value: lId === "1" ? en.Difficulty : ch.Difficulty, position: 'bottom', fontSize: 18 }}
//                   domain={[0, 5]}
//                   tickCount={6}
//                   tick={{ fontSize: 20 }}
//                   axisLine={{ stroke: '#333' }}
//                 />
//                 <YAxis
//                   type="number"
//                   dataKey="desiredseq"
//                   name="Importance"
//                   label={{ value: lId === "1" ? en.Importance : ch.Importance, angle: -90, position: 'left', fontSize: 18 }}
//                   domain={[0, 5]}
//                   tickCount={6}
//                   tick={{ fontSize: 20 }}
//                   axisLine={{ stroke: '#333' }}
//                 /> */}

//                 <ReferenceDot x={0.5} y={4.5} r={0} isFront={true}>
//                   <Label
//                     value={filteredData.filter(item => item.desiredseq >= 4 && item.currentseq <= 2 && item.desiredseq > 0 && item.currentseq > 0 && item.time > 0).length}
//                     position="center"
//                     fill="red"
//                     fontSize={24}
//                     fontWeight="bold"
//                   />
//                 </ReferenceDot>

//                 <ReferenceDot x={4.5} y={4.5} r={0} isFront={true}>
//                   <Label
//                     value={filteredData.filter(item => item.desiredseq >= 4 && item.currentseq > 2 && item.desiredseq > 0 && item.currentseq > 0 && item.time > 0).length}
//                     position="center"
//                     fill="red"
//                     fontSize={24}
//                     fontWeight="bold"
//                   />
//                 </ReferenceDot>

//                 <ReferenceDot x={0.5} y={0.5} r={0} isFront={true}>
//                   <Label
//                     value={filteredData.filter(item => item.desiredseq <= 3 && item.desiredseq > 0 && item.currentseq <= 2 && item.currentseq > 0 && item.time > 0).length}
//                     position="center"
//                     fill="red"
//                     fontSize={24}
//                     fontWeight="bold"
//                   />
//                 </ReferenceDot>

//                 <ReferenceDot x={4.5} y={0.5} r={0} isFront={true}>
//                   <Label
//                     value={filteredData.filter(item => item.desiredseq <= 3 && item.currentseq > 2 && item.desiredseq > 0 && item.currentseq > 0 && item.time > 0).length}
//                     position="center"
//                     fill="red"
//                     fontSize={24}
//                     fontWeight="bold"
//                   />
//                 </ReferenceDot>

//                 {/* <text x="15%" y="18%" textAnchor="middle" fontSize="20" fontWeight="bold" fill="red">
//                   {lId === "1" ? en.EasynImportant : ch.EasynImportant}
//                 </text>
//                 <text x="90%" y="18%" textAnchor="middle" fontSize="20" fontWeight="bold" fill="red">
//                   {lId === "1" ? en.ImportantnDifficult : ch.ImportantnDifficult}
//                 </text>
//                 <text x="15%" y="88%" textAnchor="middle" fontSize="20" fontWeight="bold" fill="red">
//                   {lId === "1" ? en.SecondarynEasy : ch.SecondarynEasy}
//                 </text>
//                 <text x="90%" y="88%" textAnchor="middle" fontSize="20" fontWeight="bold" fill="red">
//                   {lId === "1" ? en.SecondarynDifficult : ch.SecondarynDifficult}
//                 </text> */}

//                 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
//                 {/* Z Axis - Importance (for bubble size) */}
//                 <ZAxis
//                   //  dataKey="desiredseq"
//                   dataKey={zAxis}
//                   scale="linear"
//                   range={[50, 12000]}
//                 />
//                 {/* <ZAxis dataKey="time" scale="linear" range={[50, 12000]} /> */}
//                 <Tooltip content={<CustomTooltip />} />
//                 <Scatter
//                   name="Opportunities"
//                   // data={filteredData}
//                   data={filteredData.filter(item =>
//                     item.currentseq > 0 &&
//                     item.desiredseq > 0 &&
//                     item.time > 0
//                   )}
//                   fill="none"
//                   stroke="black"
//                   strokeWidth={2}
//                 />
//               </ScatterChart>
//             </ResponsiveContainer>
//           </div>
//           <div className="flex gap-4 mt-4 ml-4 text-3xl">
//             <div>
//               <label>X-Axis:</label>
//               <select value={xAxis} onChange={(e) => setXAxis(e.target.value)}>
//                 {axisOptions.map(opt => (
//                   <option key={`x-${opt.value}`} value={opt.value}>{opt.label}</option>
//                 ))}
//               </select>
//             </div>

//             <div>
//               <label>Y-Axis:</label>
//               <select value={yAxis} onChange={(e) => setYAxis(e.target.value)}>
//                 {axisOptions.map(opt => (
//                   <option key={`y-${opt.value}`} value={opt.value}>{opt.label}</option>
//                 ))}
//               </select>
//             </div>

//             <div>
//               <label>Size (Z-Axis):</label>
//               <select value={zAxis} onChange={(e) => setZAxis(e.target.value)}>
//                 {axisOptions.map(opt => (
//                   <option key={`z-${opt.value}`} value={opt.value}>{opt.label}</option>
//                 ))}
//               </select>
//             </div>
//           </div>
//         </div>

//         {/* Data table section */}
//         <div className="bg-white rounded-xl shadow-md p-4">
//           <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
//             <table className="min-w-full divide-y divide-gray-200">
//               {/* <thead className="bg-gray-50 sticky top-0">
//                 <tr>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.QID : ch.QID}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.Method : ch.Method}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.enabler_ln : ch.enabler_ln}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.question : ch.question}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.desired : ch.desired}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.Difficulty : ch.Difficulty}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.Importance : ch.Importance}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.Time : ch.Time}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? "Rank" : "排名"}</th>
//                   <th className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">{lId === "1" ? en.remarks : ch.remarks}</th>
//                 </tr>
//               </thead> */}

//               {/* sorting */}

//               <thead className="bg-gray-50 sticky top-0">
//                 <tr>
//                   <th style={{ width: 70 }}
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('qid')}
//                   >
//                     {lId === "1" ? en.QID : ch.QID}
//                     {sortConfig.key === 'qid' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th style={{ width: 114 }}
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('method')}
//                   >
//                     {lId === "1" ? en.Method : ch.Method}
//                     {sortConfig.key === 'method' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('enablers')}
//                   >
//                     {lId === "1" ? en.enabler_ln : ch.enabler_ln}
//                     {sortConfig.key === 'enablers' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('questions')}
//                   >
//                     {lId === "1" ? en.question : ch.question}
//                     {sortConfig.key === 'questions' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('desired')}
//                   >
//                     {lId === "1" ? en.desired : ch.desired}
//                     {sortConfig.key === 'desired' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th style={{ width: 140 }}
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('currentseq')}
//                   >
//                     {lId === "1" ? en.Difficulty : ch.Difficulty}
//                     {sortConfig.key === 'currentseq' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th style={{ width: 152 }}
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('desiredseq')}
//                   >
//                     {lId === "1" ? en.Importance : ch.Importance}
//                     {sortConfig.key === 'desiredseq' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th style={{ width: 80 }}
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('time')}
//                   >
//                     {lId === "1" ? en.Time : ch.Time}
//                     {sortConfig.key === 'time' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th style={{ width: 90 }}
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
//                     onClick={() => requestSort('rank')}
//                   >
//                     {lId === "1" ? "Rank" : "排名"}
//                     {sortConfig.key === 'rank' && (
//                       <span>{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
//                     )}
//                   </th>
//                   <th style={{ width: 200 }}
//                     className="px-4 py-2 text-left text-3xl font-medium text-gray-500 uppercase">
//                     {lId === "1" ? en.remarks : ch.remarks}
//                   </th>
//                 </tr>
//               </thead>


//               <tbody className="bg-white divide-y divide-gray-200">
//                 {/* {filteredData?.map((item) => ( */}
//                 {sortedData?.map((item) => (
//                   <tr key={item.index}>
//                     <td className="px-4 py-2 whitespace-nowrap text-2xl text-gray-900">{item.qid}</td>
//                     <td className="px-4 py-2 whitespace-nowrap text-2xl text-gray-900">{item.method}</td>
//                     <td className="px-4 py-2 whitespace-nowrap text-2xl text-gray-900">{item.enablers}</td>
//                     <td className="px-4 py-2 whitespace-normal text-2xl text-gray-900">{item.questions}</td>
//                     <td className="px-4 py-2 whitespace-normal text-2xl text-gray-900">{item.desired}</td>
//                     <td className="px-4 py-2 whitespace-nowrap">
//                       <select
//                         value={item.currentseq || ''}
//                         onChange={(e) => handleDataChange(item, 'currentseq', e.target.value)}
//                         className="w-full border rounded px-2 py-1 text-2xl"
//                       >
//                         <option value="">{lId === "1" ? en.Select : ch.Select}</option>
//                         {[1, 2, 3, 4, 5].map(num => (
//                           <option key={`difficulty-${num}`} value={num}>{num}</option>
//                         ))}
//                       </select>
//                     </td>
//                     <td className="px-4 py-2 whitespace-nowrap">
//                       <select
//                         value={item.desiredseq || ''}
//                         onChange={(e) => handleDataChange(item, 'desiredseq', e.target.value)}
//                         className="w-full border rounded px-2 py-1 text-2xl"
//                       >
//                         <option value="">{lId === "1" ? en.Select : ch.Select}</option>
//                         {[1, 2, 3, 4, 5].map(num => (
//                           <option key={`importance-${num}`} value={num}>{num}</option>
//                         ))}
//                       </select>
//                     </td>
//                     <td className="px-4 py-2 whitespace-nowrap">
//                       <select
//                         value={item.time || ''}
//                         onChange={(e) => handleDataChange(item, 'time', e.target.value)}
//                         className="w-full border rounded px-2 py-1 text-2xl"
//                       >
//                         <option value="">{lId === "1" ? en.Select : ch.Select}</option>
//                         {[1, 2, 3, 4, 5].map(num => (
//                           <option key={`time-${num}`} value={num}>{num}</option>
//                         ))}
//                       </select>
//                     </td>
//                     <td className="px-4 py-2 whitespace-nowrap text-2xl text-center">
//                       {calculateRank(item)}
//                     </td>
//                     <td className="px-4 py-2 whitespace-nowrap">
//                       <input
//                         type="text"
//                         value={item.remarks || ''}
//                         onChange={(e) => handleDataChange(item, 'remarks', e.target.value)}
//                         className="w-full border rounded px-2 py-1 text-2xl"
//                         placeholder={lId === "1" ? en.EnterRemarks : ch.EnterRemarks}
//                       />
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Matrix and Summary sections */}

//         {assessmentId === "2" && user_Type === "Bottler" ?
//           <div className="bg-white rounded-xl shadow-md p-4 mt-6 flex">
//             <div className="w-3/4 pr-6">
//               <div className="bg-white rounded-xl shadow-md">
//                 <h2 className="text-4xl font-semibold mb-4">{lId === "1" ? en.OpportunityCountMatrix : ch.OpportunityCountMatrix}</h2>
//                 <div className="overflow-x-auto">
//                   <table className="min-w-full border-collapse">
//                     <thead>
//                       <tr>
//                         <th className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100" rowSpan="2">{lId === "1" ? en.Importance : ch.Importance}</th>
//                         <th className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100" colSpan="5">{lId === "1" ? en.Difficulty : ch.Difficulty}</th>
//                       </tr>
//                       <tr>
//                         {[1, 2, 3, 4, 5].map(num => (
//                           <th key={`difficulty-${num}`} className="border px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-100">{num}</th>
//                         ))}
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {[5, 4, 3, 2, 1].map(importance => (
//                         <tr key={`importance-${importance}`}>
//                           <td className="border px-4 py-2 text-2xl text-center font-medium text-gray-700 bg-gray-100">{importance}</td>
//                           {[1, 2, 3, 4, 5].map(difficulty => {
//                             const count = filteredData.filter(item =>
//                               item.desiredseq === importance && item.currentseq === difficulty
//                               &&
//                               item.time > 0
//                             ).length;

//                             let bgColor = '';
//                             if (importance >= 4 && difficulty <= 2) bgColor = 'bg-blue-100';
//                             else if (importance >= 4 && difficulty > 2) bgColor = 'bg-yellow-100';
//                             else if (importance <= 3 && difficulty <= 2) bgColor = 'bg-green-500';
//                             else bgColor = 'bg-red-100';

//                             return (
//                               <td
//                                 key={`cell-${importance}-${difficulty}`}
//                                 className={`border px-4 py-2 text-2xl text-center ${bgColor}`}
//                               >
//                                 {count > 0 ? count : ''}
//                               </td>
//                             );
//                           })}
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               </div>
//             </div>

//             <div className="w-1/4">
//               <h2 className="text-4xl font-semibold mb-4">{lId === "1" ? en.QuadrantSummary : ch.QuadrantSummary}</h2>
//               <table className="min-w-full border-collapse">
//                 <thead>
//                   <tr>
//                     <th className="border border-gray-400 px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-200">{lId === "1" ? en.Quadrant : ch.Quadrant}</th>
//                     <th className="border border-gray-400 px-4 py-2 text-2xl font-medium text-gray-700 bg-gray-200">{lId === "1" ? en.Count : ch.Count}</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   <tr>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl bg-blue-100">{lId === "1" ? en.EasynImportant : ch.EasynImportant}</td>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-blue-100">
//                       {filteredData.filter(item => item.desiredseq >= 4 && item.currentseq <= 2 && item.currentseq > 0 && item.desiredseq > 0 && item.time > 0).length}
//                     </td>
//                   </tr>
//                   <tr>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl bg-yellow-100">{lId === "1" ? en.ImportantnDifficult : ch.ImportantnDifficult}</td>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-yellow-100">
//                       {filteredData.filter(item => item.desiredseq >= 4 && item.currentseq > 2 && item.currentseq > 0 && item.desiredseq > 0 && item.time > 0).length}
//                     </td>
//                   </tr>
//                   <tr>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl bg-green-500">{lId === "1" ? en.SecondarynEasy : ch.SecondarynEasy}</td>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-green-500">
//                       {filteredData.filter(item => item.desiredseq <= 3 && item.desiredseq > 0 && item.currentseq <= 2 && item.currentseq > 0 && item.time > 0).length}
//                     </td>
//                   </tr>
//                   <tr>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl bg-red-100">{lId === "1" ? en.SecondarynDifficult : ch.SecondarynDifficult}</td>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl text-center bg-red-100">
//                       {filteredData.filter(item => item.desiredseq <= 3 && item.currentseq > 2 && item.currentseq > 0 && item.desiredseq > 0 && item.time > 0).length}
//                     </td>
//                   </tr>
//                   <tr>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl font-medium bg-gray-200">{lId === "1" ? en.total : ch.total}</td>
//                     <td className="border border-gray-400 px-4 py-2 text-2xl text-center font-medium bg-gray-200">
//                       {filteredData.filter(item =>
//                         item.currentseq > 0 && item.currentseq != null &&
//                         item.desiredseq > 0 && item.desiredseq != null &&
//                         item.time > 0
//                       ).length}
//                     </td>
//                   </tr>
//                 </tbody>
//               </table>
//             </div>
//           </div>
//           :
//           null
//         }



//         {/* <div className="bg-white rounded-xl shadow-md p-4 mt-6">
//           <h2 className="text-3xl font-semibold mb-4">
//             {lId === "1" ? "Copy Assessment" : "复制评估"}
//           </h2>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
//             <div>
//               <label className="block text-2xl font-medium text-gray-700 mb-1">
//                 {lId === "1" ? "Select User" : "选择用户"}
//               </label>
//               <select
//                 value={selectedUserId}
//                 onChange={(e) => setSelectedUserId(e.target.value)}
//                 className="w-full p-2 border rounded text-2xl"
//               >
//                 <option value="">{lId === "1" ? "Select User" : "选择用户"}</option>
//                 {users.map(user => (
//                   <option key={user.uId} value={user.uId}>
//                     {user.userName} ({user.name})
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div>
//               <label className="block text-2xl font-medium text-gray-700 mb-1">
//                 {lId === "1" ? "Select Assessment" : "选择评估"}
//               </label>
//               <select
//                 value={selectedAssessmentId}
//                 onChange={(e) => setSelectedAssessmentId(e.target.value)}
//                 className="w-full p-2 border rounded text-2xl"
//               >
//                 <option value="">{lId === "1" ? "Select Assessment" : "选择评估"}</option>
//                 <option value="1">{lId === "1" ? "Self Assessment" : "自我评估"}</option>
//                 <option value="2">{lId === "1" ? "Review Assessment" : "审查评估"}</option>
//                 <option value="3">{lId === "1" ? "Final Evaluation" : "最终评估"}</option>
//               </select>
//             </div>

//             <div>
//               <button
//                 onClick={() => {
//                   if (!selectedUserId || !selectedAssessmentId) {
//                     alert(lId === "1"
//                       ? "Please select both user and assessment"
//                       : "请同时选择用户和评估");
//                     return;
//                   }
//                   setShowConfirmationModal(true);
//                 }}
//                 disabled={!selectedUserId || !selectedAssessmentId}
//                 className={`px-6 py-3 rounded text-2xl text-white ${!selectedUserId || !selectedAssessmentId
//                   ? 'bg-gray-400 cursor-not-allowed'
//                   : 'bg-blue-500 hover:bg-blue-600'
//                   }`}
//               >
//                 {lId === "1" ? "Copy Assessment" : "复制评估"}
//               </button>
//             </div>
//           </div>
//         </div> */}
//       </div>

//       {/* Render the modal when needed */}
//       {isHelpModalOpen && <HelpModal />}

//       {showConfirmationModal && <ConfirmationModal />}
//     </div>
//   );
// };

// export default OpportunityBubbleChart;
